import React, { FC, useCallback, useContext, useMemo } from 'react';
import { OrganizationUsersFetcher } from '../fetchers/OrganizationUsersFetcher';
import { UrlQueryDataGrid } from '../components/UrlQueryDataGrid/UrlQueryDataGrid';
import { GridColumns, GridRowParams } from '@mui/x-data-grid';
import { getStringEqualsOperator } from '../filterOperators/stringEqualsOperator';
import { getStringContainsOperator } from '../filterOperators/stringContainsOperator';
import { getStringDiffersOperator } from '../filterOperators/stringDiffersOperator';
import { CellContentTooltip } from '../components/CellContentTooltip/CellContentTooltip';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader/PageHeader';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { AuthenticatedRoutes } from '../routers/AuthenticatedRoutes';
import { getDateValueFormatter } from '../valueFormatters/DateValueFormatter';
import { DateFormat } from '../helpers/dateFormat';
import { getDateAfterFilterOperator, getDateBeforeFilterOperator } from '../filterOperators/dateFilterOperators';
import { InviteButton } from '../components/Invites/InviteButton';
import { EasyFilter } from '../components/EasyFilter/EasyFilter';
import { useIsGuardedRouteAllowed } from '../resources/GuardedRouteLink';
import { UserRoleInOrganizationChip } from '../components/UserRoleInOrganization/UserRoleInOrganizationChip';
import { ChangeUserRoleInOrganizationDialog } from '../components/UserRoleInOrganization/ChangeUserRoleInOrganizationDialog';
import { apiLicense } from '../resources/api/apiLicense';
import { useResourceChecker } from '../resources/resource';
import { RowButtonsContainer } from '../components/RowButtonsContainer/RowButtonsContainer';
import Button from '@mui/material/Button';
import { Link, useHistory } from 'react-router-dom';
import { queryUrl } from '../hooks/useQueryUrl';
import { AssignLicensesDialog } from '../components/AssignLicenses/AssignLicensesDialog';
import { useQueryParam } from '../hooks/useQueryParam';
import { UserContextProvider } from '../contexts/UserContextProvider';
import { useCloseDialog } from '../hooks/useCloseDialog';

const { useAssignLicense } = apiLicense;

const CurrentOrganizationUsers = () => {
  const history = useHistory();
  const { t } = useTranslation();

  const { organization } = useContext(OrganizationContext);

  const { resource: resourceAssignLicense } = useAssignLicense(organization ?? void 0, 'any');
  const checker = useResourceChecker();
  const isAssignLicenseAllowed = checker(resourceAssignLicense);

  const { data } = useContext(OrganizationUsersFetcher.Context);

  const rows = data ? data.payload.data : null;

  const getRowLink = useCallback((params: GridRowParams) => {
    return AuthenticatedRoutes.UserOrganization(organization?.name, params.row.id);
  }, []);

  const isGetRowLinkAllowed = useIsGuardedRouteAllowed('UserOrganization', [organization?.name, 'any']);

  const assignId = useQueryParam('assign');
  const assignUser = data?.payload.data.find(({ id }) => id === assignId);
  const closeDialog = useCloseDialog(void 0, ['assign']);

  const columns = useMemo<GridColumns>(() => {
    return [
      {
        field: 'name',
        headerName: t('common.tableHeader.name'),
        flex: 2,
        filterable: false,
        sortable: false,
        renderCell: (params) => (
          <CellContentTooltip>
            {params.row.first_name} {params.row.last_name}
          </CellContentTooltip>
        ),
      },
      {
        field: 'first_name',
        hide: true,
        hideable: false,
        headerName: t('common.tableHeader.firstName'),
        filterOperators: [getStringContainsOperator(), getStringEqualsOperator(), getStringDiffersOperator()],
      },
      {
        field: 'last_name',
        hide: true,
        hideable: false,
        headerName: t('common.tableHeader.lastName'),
        filterOperators: [getStringContainsOperator(), getStringEqualsOperator(), getStringDiffersOperator()],
      },
      {
        field: 'email',
        headerName: t('common.tableHeader.email'),
        flex: 3,
        filterOperators: [getStringContainsOperator(), getStringEqualsOperator()],
        renderCell: (params) => <CellContentTooltip>{params.value}</CellContentTooltip>,
      },
      {
        field: 'roles',
        headerName: t('common.tableHeader.role'),
        width: 120,
        filterable: false,
        sortable: false,
        renderCell: (params) => {
          return <UserRoleInOrganizationChip user={params.row} />;
        },
      },
      {
        field: 'phone_number',
        headerName: t('common.tableHeader.phone'),
        width: 120,
        filterOperators: [getStringContainsOperator(), getStringEqualsOperator()],
        renderCell: (params) => <CellContentTooltip>{params.value}</CellContentTooltip>,
      },
      {
        field: 'created_at',
        headerName: t('common.tableHeader.created'),
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        width: 130,
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
      {
        field: 'updated_at',
        headerName: t('common.tableHeader.updated'),
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        width: 130,
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
      {
        field: 'buttons',
        hide: !isAssignLicenseAllowed,
        headerName: ' ',
        sortable: false,
        filterable: false,
        width: 150,
        renderCell: (params) => {
          const url = queryUrl('assign', params.row.id, history.location.search);

          return (
            <RowButtonsContainer>
              <Button variant="outlined" color="primary" component={Link} to={url}>
                {t('user.assignLicensesButton')}
              </Button>
            </RowButtonsContainer>
          );
        },
      },
    ];
  }, [t, isAssignLicenseAllowed]);

  if (!rows || !data) return null;
  return (
    <>
      <UrlQueryDataGrid
        columns={columns}
        rows={rows}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        paginationMode={'server'}
        filterMode={'server'}
        sortingMode={'server'}
        rowCount={data.payload.total_items}
        getRowLink={isGetRowLinkAllowed ? getRowLink : void 0}
      />

      {assignUser ? (
        <UserContextProvider user={assignUser}>
          <AssignLicensesDialog open={true} closeDialog={closeDialog} />
        </UserContextProvider>
      ) : null}
    </>
  );
};

export const OrganizationUsersPage: FC = () => {
  const { t } = useTranslation();
  const { data, onRefresh } = useContext(OrganizationUsersFetcher.Context);

  return (
    <div>
      <PageHeader title={t('layout.navigation.organizationUsers')}>
        <InviteButton onDone={onRefresh} />
      </PageHeader>

      <EasyFilter />
      <CurrentOrganizationUsers />
      {data ? <ChangeUserRoleInOrganizationDialog users={data.payload.data} onRefresh={onRefresh} /> : null}
    </div>
  );
};
